import axios from 'axios';
import React from 'react';
import NavLink from '../Utils/NavLink/navlink-component';
import { ServerEndpoint } from '../Redux/endpoints.ts';

// Current language sent with every request so the backend picks the right system_data file.
// Updated by useLanguageParam whenever the app language changes.
let _apiLanguage = 'en';
export function setApiLanguage(lang) { _apiLanguage = lang; }

export function request(path, method = 'GET', content = {}, pending = false, pendingMessage = 'Loading...') {
    // Detect FormData and send without JSON.stringify or content-type override
    const isFormData = content instanceof FormData;

    return axios({
        method,
        url: process.env.REACT_APP_INFRA_BACK_ENDPOINT + path,
        data: isFormData ? content : JSON.stringify(content),
        headers: isFormData
            ? { 'X-Language': _apiLanguage }
            : { 'Content-Type': 'application/json', 'X-Language': _apiLanguage },
    });
}


export function get(path, pending = false, pendingMessage = 'Loading...') {
    return request(path, 'GET', {}, pending, pendingMessage);
}


export function patch(path, content = {}) {
    return request(path, 'PATCH', content);
}

export function post(path, content = {}, pending = false, pendingMessage = 'Loading...', attachments = []) {

    // If attachments are provided → always send as FormData
    if (attachments?.length > 0) {
        const form = new FormData();
        form.append('payload', JSON.stringify(content || {}));
        attachments.forEach((file) => {
            if (file) {
                form.append('attachments', file);
            }
        });
        return request(path, 'POST', form, pending, pendingMessage);
    }

    // No attachments → normal JSON flow
    return request(path, 'POST', content, pending, pendingMessage);
}


export function isValidHttpsUrl(u) {
    if (!u || typeof u !== 'string') {
        return false;
    }
    const s = u.trim();
    if (s.length <= 'https://'.length) {
        return false; // requires something after scheme
    }
    try {
        const url = new URL(s);
        return url.protocol === 'https:';
    } catch {
        return false;
    }
}


/**
 * Checks if a string is a valid URL, supporting both protocol-led (https://)
 * and shorthand (www.) links.
 */

export function isValidUrl(u) {
    if (!u || typeof u !== 'string') return false;
    const s = u.trim();
    
    // Check for protocol-less www. links
    if (/^www\./i.test(s)) return true;

    try {
        const url = new URL(s);
        return ['http:', 'https:', 'mailto:', 'tel:'].includes(url.protocol);
    } catch {
        return false;
    }
}

/**
 * Ensures a URL has a protocol for the <a> tag's href attribute.
 */
export function ensureProtocol(url) {
    if (/^www\./i.test(url)) return `https://${url}`;
    return url;
}

/**
 * Validates video extensions
 */
export function isVideoUrl(url) {
    if (!url || typeof url !== 'string') return false;
    return /\.(mp4|mov|webm|ogg|m4v)$/i.test(url);
};

/**
 * Rewrites our S3 asset URLs to the backend's media route so every media
 * request (and the URL Google indexes) is on our own domain instead of the
 * raw S3 endpoint. External URLs pass through unchanged.
 */
export function toMediaUrl(src) {
    if (!src || typeof src !== 'string') return src;
    const assetsEndpoint = process.env.REACT_APP_INFRA_ASSETS_ENDPOINT;
    if (!assetsEndpoint || !src.startsWith(assetsEndpoint)) return src;
    const key = src
        .slice(assetsEndpoint.length)
        .replace(/^\//, '')
        .replace(/^sawpy-assets\//, '');
    return `${process.env.REACT_APP_INFRA_BACK_ENDPOINT}${ServerEndpoint.MEDIA}/${key}`;
}

/**
 * Absolute form of a media URL — for surfaces that can't resolve relative
 * paths: og:image tags, copied/shareable links, emails.
 * Uses the backend origin: identical to the site origin in production (the
 * backend serves the SPA), and the directly-openable :<backend port> in dev,
 * where the CRA proxy skips address-bar (text/html) navigations.
 */
export function toAbsoluteMediaUrl(src) {
    const url = toMediaUrl(src);
    if (!url || typeof url !== 'string' || !url.startsWith('/')) return url;
    const backendOrigin = (process.env.REACT_APP_INFRA_BACK_ENDPOINT || '').replace(/\/$/, '');
    return `${backendOrigin}${url}`;
}

export function extractYouTubeId(url) {
    if (!url) return null;
    const match = url.match(
        /(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/
    );
    return match ? match[1] : null;
}

export function extractYouTubeUrlsFromContent(content) {
    if (!content) return [];
    const urls = [];
    const mdRegex = /\[([^\]]*)\]\((https?:\/\/(?:www\.)?(?:youtube\.com|youtu\.be)[^\)]+)\)/g;
    let m;
    while ((m = mdRegex.exec(content)) !== null) {
        if (extractYouTubeId(m[2])) urls.push(m[2]);
    }
    const bareRegex = /(?<!\()(https?:\/\/(?:www\.)?(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)[A-Za-z0-9_?=&-]+)/g;
    while ((m = bareRegex.exec(content)) !== null) {
        if (extractYouTubeId(m[1]) && !urls.includes(m[1])) urls.push(m[1]);
    }
    return urls;
}


// ─── Markdown / link text renderer ───────────────────────────────────────────

const SITE_HOST  = process.env.REACT_APP_SITE_HOST || 'flowma.co.il';
const LINK_CLASS = 'underline font-bold text-primary-600 decoration-1 underline-offset-4 hover:opacity-80 transition-opacity';

const makeTokenRegex = () =>
    /\[([^\]]+)\]\((https?:\/\/[^)\s]+)\)|(https?:\/\/[^\s)]+|www\.[^\s)]+|mailto:[^\s@]+@[^\s@]+\.[^\s@]+|tel:\+?[0-9.-]{7,15})/gi;

const isSameDomain = (url) => {
    try { return new URL(url).hostname.replace(/^www\./, '').endsWith(SITE_HOST); }
    catch { return false; }
};

const extractPath = (url) => {
    try { const u = new URL(url); return u.pathname + u.search + u.hash || '/'; }
    catch { return url; }
};

export const parseSegments = (line) => {
    const parts = [];
    let lastIndex = 0;
    const regex = makeTokenRegex();
    let match;

    while ((match = regex.exec(line)) !== null) {
        if (match.index > lastIndex) {
            parts.push(<span key={`t${lastIndex}`}>{line.slice(lastIndex, match.index)}</span>);
        }

        const isMarkdown = !!(match[1] && match[2]);
        const rawUrl     = isMarkdown ? match[2] : match[3];
        const display    = isMarkdown ? match[1] : rawUrl.replace(/^(mailto:|tel:)/i, '');
        const internal   = isSameDomain(rawUrl);
        const href       = internal ? extractPath(rawUrl) : ensureProtocol(rawUrl);

        parts.push(
            <NavLink
                key={`l${match.index}`}
                href={href}
                title={display}
                target={internal ? undefined : '_blank'}
                className={LINK_CLASS}
            >
                {display}
            </NavLink>
        );

        lastIndex = match.index + match[0].length;
    }

    if (lastIndex < line.length) {
        parts.push(<span key={`t${lastIndex}`}>{line.slice(lastIndex)}</span>);
    }

    return parts;
};

export const renderParagraphs = (text, textClassName = '') => {
    if (!text) return null;
    return String(text)
        .split('\n\n')
        .map((para, idx) => (
            <p key={idx} className={`mb-4 last:mb-0 leading-relaxed whitespace-pre-line ${textClassName}`}>
                {parseSegments(para)}
            </p>
        ));
};


export async function downloadFile(path, filename) {
    const res  = await axios.get(process.env.REACT_APP_INFRA_BACK_ENDPOINT + path, { responseType: 'blob' });
    const url  = window.URL.createObjectURL(res.data);
    const link = document.createElement('a');
    link.href     = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
}

export function safeOpenUrl(u, e) {
    if (e && typeof e.preventDefault === 'function') {
        e.preventDefault();
    }
    if (!isValidHttpsUrl(u)) {
        return;
    }
    window.open(u.trim(), '_blank', 'noopener');
}
